import { formatAadhaarDisplay, formatInrAmount } from "../../config/digitalCareAgreement";
import "./DigitalCareAgreementPreview.css";

function PinIcon() {
  return (
    <svg className="dca__pin" viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
      <path
        fill="currentColor"
        d="M12 2.5c-3.4 0-6.2 2.7-6.2 6.1 0 4.6 6.2 12.9 6.2 12.9s6.2-8.3 6.2-12.9c0-3.4-2.8-6.1-6.2-6.1zm0 8.3a2.2 2.2 0 110-4.4 2.2 2.2 0 010 4.4z"
      />
    </svg>
  );
}

function ScopeList({ items }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <ul className="dca__scope-list">
      {items.map((item) => (
        <li key={item}>{item}</li>
      ))}
    </ul>
  );
}

export default function DigitalCareAgreementPreview({
  agreement,
  compact = false,
  acknowledged = false,
  onAcknowledgedChange,
  onBack,
  onContinue,
  continueLabel = "I agree & continue",
  busy = false,
  showActions = true,
}) {
  if (!agreement) return null;

  const monthlyLabel = formatInrAmount(agreement.monthlySalaryInr);
  const dailyLabel = formatInrAmount(agreement.dailyRateInr);
  const hourlyLabel = formatInrAmount(agreement.hourlyRateInr);
  const visitLabel = formatInrAmount(agreement.visitFeeInr);
  const isHourlyAgreement =
    agreement.templateKey === "hourly_companion" ||
    agreement.templateKey === "hourly_hygiene" ||
    agreement.templateKey === "nurse_hourly" ||
    agreement.templateKey === "physio_hourly" ||
    String(agreement.shiftKey || "").toLowerCase() === "hourly";
  const providerLabel = agreement.caregiverRole || "Caregiver";
  const extraSections = Array.isArray(agreement.extraSections) ? agreement.extraSections : [];
  const salaryMode =
    agreement.salaryBillingMode === "daily" || agreement.salaryBillingMode === "monthly"
      ? agreement.salaryBillingMode
      : dailyLabel && !monthlyLabel
        ? "daily"
        : monthlyLabel
          ? "monthly"
          : null;
  const scopeItems = Array.isArray(agreement.scopeItems)
    ? agreement.scopeItems
    : Array.isArray(agreement.dutyClauses)
      ? agreement.dutyClauses.map((c) => (typeof c === "string" ? c : c?.body)).filter(Boolean)
      : [];
  const conductItems = Array.isArray(agreement.conductItems) ? agreement.conductItems : [];
  const shiftTiming =
    agreement.shiftTimingDisplay ||
    `${agreement.shiftLabel || "—"}${
      agreement.startDate ? ` (Starts: ${String(agreement.startDate).slice(0, 10)})` : ""
    }`;

  return (
    <div className={`dca${compact ? " dca--compact" : ""}`}>
      <article className="dca__sheet" aria-label="NEMA Digital Care Agreement">
        <header className="dca__head">
          <p className="dca__kicker">Care contract</p>
          <h2 className="dca__brand">{agreement.title || "NEMA DIGITAL CARE AGREEMENT"}</h2>
          <p className="dca__contract">{agreement.contractTitle}</p>
        </header>

        <div className="dca__body">
          <dl className="dca__meta">
            <div>
              <dt>Agreement Reference</dt>
              <dd>{agreement.ref}</dd>
            </div>
            <div>
              <dt>Selected Caregiver</dt>
              <dd>
                {agreement.caregiverName}
                {agreement.caregiverRole ? ` (${agreement.caregiverRole})` : ""}
              </dd>
            </div>
            <div>
              <dt>Shift Type &amp; Timing</dt>
              <dd>{shiftTiming}</dd>
            </div>
            <div>
              <dt>Beneficiary Patient</dt>
              <dd>
                {agreement.patientName}
                {agreement.patientId ? ` (Patient ID: ${agreement.patientId})` : ""}
              </dd>
            </div>
            {agreement.patientAadhaarMasked && agreement.patientAadhaarMasked !== "Not on file" ? (
              <div>
                <dt>Patient Aadhaar Number</dt>
                <dd>{formatAadhaarDisplay(agreement.patientAadhaarMasked)}</dd>
              </div>
            ) : null}
            <div className="dca__meta-address">
              <dt>Confirmed Service Address</dt>
              <dd>
                <PinIcon />
                <span>{agreement.serviceAddress || "Address to be confirmed"}</span>
              </dd>
            </div>
          </dl>

          {agreement.preamble ? <p className="dca__preamble">{agreement.preamble}</p> : null}

          <section className="dca__block">
            <h3>{agreement.scopeHeading || "1. Scope of Service"}</h3>
            <ScopeList items={scopeItems} />
          </section>

          <section className="dca__block">
            <h3>{agreement.billingHeading || "2. Billing"}</h3>
            <p>{agreement.billingText}</p>
            {isHourlyAgreement && hourlyLabel ? (
              <p className="dca__billing-note">
                Agreed hourly rate for this {providerLabel}: <strong>{hourlyLabel}/hr</strong>.
              </p>
            ) : isHourlyAgreement && visitLabel ? (
              <p className="dca__billing-note">
                Agreed visit fee for this booking: <strong>{visitLabel}</strong>.
              </p>
            ) : salaryMode === "daily" && dailyLabel ? (
              <p className="dca__billing-note">
                Agreed per-day salary shown for this engagement: <strong>{dailyLabel}/day</strong>.
              </p>
            ) : salaryMode === "monthly" && monthlyLabel ? (
              <p className="dca__billing-note">
                Agreed monthly salary shown for this engagement: <strong>{monthlyLabel}/mo</strong>.
              </p>
            ) : visitLabel ? (
              <p className="dca__billing-note">
                Agreed visit fee for this booking: <strong>{visitLabel}</strong>.
              </p>
            ) : null}
          </section>

          <section className="dca__block">
            <h3>{agreement.conductHeading || "3. Conduct & Responsibilities"}</h3>
            <ScopeList items={conductItems} />
          </section>

          {extraSections.map((section) => (
            <section className="dca__block" key={section.heading}>
              <h3>{section.heading}</h3>
              {Array.isArray(section.paragraphs)
                ? section.paragraphs.map((p) => (
                    <p key={p} className="dca__para">
                      {p}
                    </p>
                  ))
                : null}
              <ScopeList items={section.items} />
            </section>
          ))}

          {agreement.importantNote ? (
            <section className="dca__block dca__block--note">
              <h3>Important note</h3>
              <p>{agreement.importantNote}</p>
            </section>
          ) : null}

          {agreement.agreementVersion || agreement.customerAcceptedAt || agreement.caregiverAcceptedAt ? (
            <dl className="dca__meta dca__meta--accept">
              {agreement.agreementVersion ? (
                <div>
                  <dt>Agreement version</dt>
                  <dd>{agreement.agreementVersion}</dd>
                </div>
              ) : null}
              {agreement.agreementCreatedAt ? (
                <div>
                  <dt>Generated</dt>
                  <dd>{new Date(agreement.agreementCreatedAt).toLocaleString("en-IN")}</dd>
                </div>
              ) : null}
              <div>
                <dt>Customer acceptance</dt>
                <dd>
                  {agreement.customerAcceptedAt
                    ? new Date(agreement.customerAcceptedAt).toLocaleString("en-IN")
                    : "Pending explicit acceptance"}
                </dd>
              </div>
              <div>
                <dt>Caregiver acceptance</dt>
                <dd>
                  {agreement.caregiverAcceptedAt
                    ? new Date(agreement.caregiverAcceptedAt).toLocaleString("en-IN")
                    : "Pending (accept booking / attend duty)"}
                </dd>
              </div>
            </dl>
          ) : null}

          {agreement.footer ? <p className="dca__footer-note">{agreement.footer}</p> : null}
        </div>
      </article>

      {showActions ? (
        <div className="dca__footer">
          {onAcknowledgedChange ? (
            <label className="dca__ack">
              <input
                type="checkbox"
                checked={acknowledged}
                onChange={(e) => onAcknowledgedChange(e.target.checked)}
              />
              <span>
                I have read and accept this Digital Care Agreement for the selected caregiver, patient, and shift.
              </span>
            </label>
          ) : null}
          <div className="dca__actions">
            {onBack ? (
              <button type="button" className="dca__btn dca__btn--ghost" onClick={onBack}>
                Back
              </button>
            ) : (
              <span />
            )}
            {onContinue ? (
              <button
                type="button"
                className="dca__btn dca__btn--primary"
                disabled={busy || (onAcknowledgedChange && !acknowledged)}
                onClick={onContinue}
              >
                {busy ? "Loading…" : continueLabel}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
